import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { updateProfile } from 'firebase/auth';
import { auth, getFirebaseAuthHeaders } from '../firebase';
import { apiClient } from '../services/api';

const BACKEND = ((process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000')
  .trim()
  .replace(/\/api\/?$/, ''));

const normalizeTelegramPhone = (value) => String(value || '').replace(/\s+/g, '').trim();

const getTelegramErrorMessage = (fallback, error) => {
  const raw = String(error || fallback || '').trim();
  const lowered = raw.toLowerCase();

  if (!raw) return fallback || 'Telegram authentication failed. Please try again.';
  if (lowered.includes('country code') || lowered.includes('phone number invalid')) {
    return 'Add the country code before the number, for example +917899001234.';
  }
  if (lowered.includes('phone_code_invalid') || lowered.includes('invalid code')) {
    return 'Invalid login code. Please check the code from Telegram and try again.';
  }
  if (lowered.includes('phone_code_expired') || lowered.includes('expired')) {
    return 'The login code expired. Request a new code and try again.';
  }
  if (lowered.includes('session_password_needed') || lowered.includes('two-step') || lowered.includes('2fa')) {
    return 'Two-step verification is enabled. Enter your Telegram password to continue.';
  }
  if (lowered.includes('password') && lowered.includes('invalid')) {
    return 'Invalid Telegram password. Enter the password used for Telegram two-step verification.';
  }
  if (lowered.includes('flood') || lowered.includes('too many')) {
    return 'Too many attempts. Please wait before requesting another Telegram code.';
  }

  return raw;
};

export default function Settings() {
  const user = auth.currentUser;
  const navigate = useNavigate();

  const [displayName, setDisplayName] = useState('');
  const [nameSaving, setNameSaving] = useState(false);
  const [telegramBusy, setTelegramBusy] = useState(false);
  const [telegramPolicy, setTelegramPolicy] = useState(() => localStorage.getItem('telegram_session_policy') || 'keep');
  const [authStatus, setAuthStatus] = useState('');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [notice, setNotice] = useState({ type: '', text: '' });
  const [apiStatus, setApiStatus] = useState('checking'); // Backend API health

  // API health check
  useEffect(() => {
    const checkAPI = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/scan-reports?limit=1`, {
          headers: {
            'X-API-Key': process.env.REACT_APP_API_KEY,
          },
        });
        setApiStatus(response.ok ? 'available' : 'unavailable');
      } catch (error) {
        console.warn('Settings API check failed:', error.message);
        setApiStatus('unavailable');
      }
    };

    checkAPI();
    // No idle polling; keep one-time status check.
  }, []);

  useEffect(() => {
    setDisplayName(user?.displayName || '');
  }, [user]);

  const trimmedName = useMemo(() => displayName.trim(), [displayName]);

  const showNotice = (type, text) => {
    setNotice({ type, text });
  };

  const extractErrorMessage = async (response, fallback) => {
    try {
      const data = await response.json();
      return data?.detail || data?.message || fallback;
    } catch (e) {
      return fallback;
    }
  };

  const checkAuth = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND}/auth/status`, {
        headers: await getFirebaseAuthHeaders(),
      });
      if (!response.ok) {
        // Telegram service not available - this is expected for Firebase backend
        console.log('Telegram auth service not available');
        setAuthStatus('authorized=false');
        return; // Don't show error for missing Telegram service
      }
      const data = await response.json();
      setAuthStatus(`authorized=${Boolean(data?.authorized)}`);
      if (data?.authorized) {
        setCode('');
        setPassword('');
      }
    } catch (err) {
      console.log('Telegram service unavailable:', err.message);
      setAuthStatus('authorized=false');
      // Don't show error notice for missing Telegram service
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const sendCode = async () => {
    const cleanPhone = normalizeTelegramPhone(phone);
    if (!cleanPhone) {
      showNotice('error', 'Enter your Telegram phone number with country code, for example +917899001234.');
      return;
    }
    if (!cleanPhone.startsWith('+')) {
      showNotice('error', 'Add the country code before the number, for example +917899001234.');
      return;
    }

    try {
      setTelegramBusy(true);
      setPhone(cleanPhone);
      const headers = await getFirebaseAuthHeaders({ 'Content-Type': 'application/json' });
      const response = await fetch(`${BACKEND}/auth/send_code`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ phone: cleanPhone })
      });

      if (!response.ok) {
        if (response.status === 404) {
          showNotice('error', 'Telegram service not available. Contact system administrator.');
          return;
        }
        const message = await extractErrorMessage(response, 'Failed to send Telegram code.');
        throw new Error(getTelegramErrorMessage('Failed to send Telegram code.', message));
      }

      showNotice('success', 'Verification code sent. Check Telegram, then enter the code below.');
    } catch (err) {
      console.error('Failed to send Telegram code', err);
      if (err.message.includes('fetch')) {
        showNotice('error', 'Telegram service not available. Contact system administrator.');
      } else {
        showNotice('error', getTelegramErrorMessage('Failed to send Telegram code.', err.message));
      }
    } finally {
      setTelegramBusy(false);
    }
  };

  const confirmCode = async () => {
    const cleanPhone = normalizeTelegramPhone(phone);
    const cleanCode = code.trim();
    if (!cleanPhone) {
      showNotice('error', 'Enter your Telegram phone number with country code, for example +917899001234.');
      return;
    }
    if (!cleanPhone.startsWith('+')) {
      showNotice('error', 'Add the country code before the number, for example +917899001234.');
      return;
    }
    if (!cleanCode) {
      showNotice('error', 'Enter the Telegram verification code you received.');
      return;
    }

    try {
      setTelegramBusy(true);
      setPhone(cleanPhone);
      const headers = await getFirebaseAuthHeaders({ 'Content-Type': 'application/json' });
      const response = await fetch(`${BACKEND}/auth/confirm`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          phone: cleanPhone,
          code: cleanCode,
          password: password || undefined
        })
      });

      if (!response.ok) {
        const message = await extractErrorMessage(response, 'Telegram authorization failed.');
        throw new Error(getTelegramErrorMessage('Telegram authorization failed.', message));
      }

      showNotice('success', 'Telegram authorized successfully.');
      await checkAuth();
    } catch (err) {
      console.error('Failed to confirm Telegram code', err);
      showNotice('error', getTelegramErrorMessage('Telegram authorization failed.', err.message));
    } finally {
      setTelegramBusy(false);
    }
  };

  const handleSaveName = async () => {
    if (!auth.currentUser) {
      showNotice('error', 'Please login again to update your profile.');
      return;
    }
    if (!trimmedName) {
      showNotice('error', 'Name cannot be empty.');
      return;
    }

    try {
      setNameSaving(true);
      
      // Update Firebase Auth profile (for immediate UI consistency)
      await updateProfile(auth.currentUser, { displayName: trimmedName });
      
      // Update backend profile data (if available)
      if (apiStatus === 'available') {
        try {
          await apiClient('/officer-profiles', 'PATCH', {
            uid: auth.currentUser.uid,
            displayName: trimmedName,
            updatedAt: new Date().toISOString(),
          });
        } catch (apiError) {
          console.warn('Officer profile API update failed:', apiError);
          // Don't fail the update if backend is down
        }
      }
      
      showNotice('success', 'Officer name updated successfully.');
    } catch (err) {
      console.error('Failed to update display name', err);
      showNotice('error', 'Could not update name. Please try again.');
    } finally {
      setNameSaving(false);
    }
  };

  // const handleKeepTelegramSession = () => {
  //   localStorage.setItem('telegram_session_policy', 'keep');
  //   setTelegramPolicy('keep');
  //   showNotice('success', 'Telegram session will be kept active on this device.');
  // };

  const handleClearTelegramSession = async () => {
    try {
      setTelegramBusy(true);
      const headers = await getFirebaseAuthHeaders();
      const response = await fetch(`${BACKEND}/auth/logout`, {
        method: 'POST',
        headers,
      });

      if (!response.ok) {
        if (response.status === 404) {
          // Telegram service not available, just clear local policy
          localStorage.setItem('telegram_session_policy', 'clear');
          setTelegramPolicy('clear');
          setAuthStatus('authorized=false');
          showNotice('success', 'Telegram session policy cleared locally.');
          return;
        }
        let message = 'Failed to clear Telegram session.';
        try {
          const data = await response.json();
          message = data?.detail || message;
        } catch (e) {
          // no-op if body is not JSON
        }
        throw new Error(message);
      }

      localStorage.setItem('telegram_session_policy', 'clear');
      setTelegramPolicy('clear');
      setAuthStatus('authorized=false');
      showNotice('success', 'Telegram session cleared. Re-login to connect another number.');
    } catch (err) {
      console.error('Failed to clear Telegram session', err);
      showNotice('error', err.message || 'Failed to clear Telegram session.');
    } finally {
      setTelegramBusy(false);
    }
  };

  return (
    <div className="page-shell settings-page">
      <header className="top-bar settings-head">
        <div>
          <h1 className="page-title">Officer Preferences</h1>
          <p className="page-subtitle">Update your profile and manage Telegram session behavior.</p>
          {/* {apiStatus !== 'checking' && (
            <div style={{ 
              fontSize: '0.85rem', 
              opacity: 0.7, 
              marginTop: '0.5rem',
              color: apiStatus === 'available' ? '#28a745' : '#6c757d'
            }}>
              {apiStatus === 'available' 
                ? '✓ Backend services active' 
                : 'ℹ Profile updates will use Firebase only'
              }
            </div>
          )} */}
        </div>
      </header>

      {notice.text && (
        <div className={`settings-notice settings-notice-${notice.type === 'error' ? 'error' : 'success'}`}>
          {notice.text}
        </div>
      )}

      <div className="dash-grid settings-grid">
        <section className="card settings-card">
          <div className="section-head settings-section-head">
            <div>
              <div className="eyebrow">PROFILE</div>
              <h3 className="section-title">Change Officer Name</h3>
              <p className="muted">Name is shown in your account profile.</p>
            </div>
          </div>

          <div className="settings-field-wrap">
            <label className="settings-label" htmlFor="officerName">Officer Name</label>
            <input
              id="officerName"
              className="form-input"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Enter your display name"
              maxLength={80}
            />
          </div>

          <div className="settings-meta">
            <span className="pill">Email: {user?.email || 'Not available'}</span>
            <span className={`pill ${apiStatus === 'available' ? '' : 'pill-warn'}`}>
              Backend: {apiStatus === 'available' ? 'Synced' : 'Firebase only'}
            </span>
          </div>

          <div className="action-row settings-actions">
            <button
              type="button"
              className="primary-btn settings-save-btn"
              onClick={handleSaveName}
              disabled={nameSaving}
            >
              {nameSaving ? 'Saving Name...' : 'Save Name'}
            </button>
          </div>
        </section>

        <section className="card settings-card">
          <div className="section-head settings-section-head">
            <div>
              <div className="eyebrow">TELEGRAM</div>
              <h3 className="section-title">Session Management</h3>
              <p className="muted">Keep the current Telegram session or clear it to reconnect with another number.</p>
            </div>
          </div>

          <div className="settings-session-status">
            <span className="pill">Current Policy: {telegramPolicy === 'clear' ? 'Clear on request' : 'Keep active'}</span>
            <span className={`pill ${authStatus === 'authorized=true' ? '' : 'pill-warn'}`}>
              Telegram: {authStatus || 'Checking...'}
            </span>
            {authStatus === 'authorized=false' && (
              <span className="pill pill-info" style={{ fontSize: '0.8rem', opacity: 0.8 }}>
                ℹ Service integration pending
              </span>
            )}
          </div>

          <div className="settings-session-note">
            Clearing session disconnects Telegram scanning until you authenticate again.
          </div>

          <div className="action-row settings-actions">
            {/* <button
              type="button"
              className="ghost-btn settings-keep-btn"
              onClick={handleKeepTelegramSession}
              disabled={telegramBusy}
            >
              Keep Telegram Session
            </button> */}
            <button
              type="button"
              className="action-btn btn-danger settings-clear-btn"
              onClick={handleClearTelegramSession}
              disabled={telegramBusy}
            >
              {telegramBusy ? 'Clearing...' : 'Clear Telegram Session'}
            </button>
          </div>

          {authStatus === 'authorized=false' && (
            <div className="settings-auth-box">
              <div className="settings-auth-title">Secure Telegram Re-Login</div>
              <p className="muted">Session is unauthorized. Sign in to reconnect Telegram scanning.</p>

              <div className="settings-field-wrap">
                <label className="settings-label" htmlFor="telegramPhone">Phone Number</label>
                <input
                  id="telegramPhone"
                  className="form-input"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+91XXXXXXXXXX"
                  inputMode="tel"
                />
              </div>

              <div className="action-row settings-actions">
                <button
                  type="button"
                  className="ghost-btn settings-keep-btn"
                  onClick={sendCode}
                  disabled={telegramBusy}
                >
                  Send Code
                </button>
              </div>

              <div className="settings-field-wrap">
                <label className="settings-label" htmlFor="telegramCode">Verification Code</label>
                <input
                  id="telegramCode"
                  className="form-input"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="Enter code"
                  inputMode="numeric"
                />
              </div>

              <div className="settings-field-wrap">
                <label className="settings-label" htmlFor="telegramPassword">2FA Password (if enabled)</label>
                <input
                  id="telegramPassword"
                  className="form-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Optional"
                  type="password"
                />
              </div>

              <button
                type="button"
                className="primary-btn"
                onClick={confirmCode}
                disabled={telegramBusy}
              >
                {telegramBusy ? 'Authorizing...' : 'Authorize Telegram Access'}
              </button>
            </div>
          )}
        </section>

        <section className="card settings-card">
          <div className="section-head settings-section-head">
            <div>
              <div className="eyebrow" style={{ color: '#1d4ed8' }}>GUIDE</div>
              <h3 className="section-title">Help & App Walkthrough</h3>
              <p className="muted">Open a quick guide for setup steps and a page-by-page overview of the app.</p>
            </div>
          </div>

          <div className="action-row settings-actions">
            <button
              type="button"
              className="ghost-btn settings-keep-btn"
              onClick={() => navigate('/guide')}
            >
              Open User Guide
            </button>
          </div>
        </section>

        <section className="card settings-card">
          <div className="section-head settings-section-head">
            <div>
              <div className="eyebrow">ADVANCED</div>
              <h3 className="section-title">State Info Management</h3>
              <p className="muted">Open the optional state info editor only when you need to manage location keyword datasets.</p>
            </div>
          </div>

          <div className="action-row settings-actions">
            <button
              type="button"
              className="ghost-btn settings-keep-btn"
              onClick={() => navigate('/state-info')}
            >
              Open State Info Editor
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}

